import React, { useEffect, useMemo, useState } from 'react';
import {
  Edit2,
  MapPin,
  Phone,
  User,
  DollarSign,
  Clock,
  Image as ImageIcon,
  FileSpreadsheet,
  Printer,
  Search,
  Truck,
  X,
  MessageCircle,
  Navigation,
  Package,
  FileText,
  Zap,
  ChevronRight,
  History,
  AlertCircle,
} from 'lucide-react';
import ImageCarouselModal from './ImageCarouselModal';
import { DownloadExcelAnalytics, DownloadExcelProvinciaAnalytics } from './DownloadExcelAnalytics';

function DeliveryList({
  deliveries,
  deliveriesProvince,
  orders,
  canEdit,
  onEdit,
  loading,
  selectedDate,
  setActiveComponent,
  expressDeliveries = [],
  userId,
}) {
  const [selectedImages, setSelectedImages] = useState(null);
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [deliveryType, setDeliveryType] = useState('lima');
  const [statusHistory, setStatusHistory] = useState([]);
  const [statusHistoryLoading, setStatusHistoryLoading] = useState(false);
  const [statusHistoryError, setStatusHistoryError] = useState('');
  const [showStatusHistory, setShowStatusHistory] = useState(false);

  const currentDeliveries = deliveryType === 'lima' ? deliveries : deliveriesProvince;
  const apiUrl = process.env.REACT_APP_API_URL || '';

  useEffect(() => {
    setStatusHistory([]);
    setStatusHistoryError('');
    setStatusHistoryLoading(false);
    setShowStatusHistory(false);
  }, [selectedDetail?.type, selectedDetail?.item?.id]);

  const filteredDeliveries = useMemo(() => {
    if (deliveryType === 'express') return [];
    if (!searchTerm.trim()) return currentDeliveries;

    const searchLower = searchTerm.toLowerCase().trim();
    return currentDeliveries.filter((delivery) => {
      const idMatch = delivery.id.toString().includes(searchLower);
      const nameMatch = delivery.recipient_name?.toLowerCase().includes(searchLower);
      const phoneMatch = delivery.recipient_phone?.toLowerCase().includes(searchLower);

      if (deliveryType === 'provincia') {
        const dniMatch = delivery.recipient_dni?.toLowerCase().includes(searchLower);
        const provinceMatch = delivery.province?.toLowerCase().includes(searchLower);
        const agencyMatch = delivery.agency_address?.toLowerCase().includes(searchLower);
        return idMatch || nameMatch || phoneMatch || dniMatch || provinceMatch || agencyMatch;
      }

      const districtMatch = delivery.district?.toLowerCase().includes(searchLower);
      return idMatch || nameMatch || phoneMatch || districtMatch;
    });
  }, [currentDeliveries, searchTerm, deliveryType]);

  function clearSearch() {
    setSearchTerm('');
  }

  function cleanPhone(phone) {
    return phone?.replace(/[^\d]/g, '') || '';
  }

  function handleWhatsAppClick(delivery) {
    const phone = cleanPhone(delivery.recipient_phone);
    const message = `¡Hola ${delivery.recipient_name}! 👋

Tu pedido está siendo procesado para entrega 📦

🚀 *Seguimiento de tu pedido*
Hemos contratado a Fast Max Courier para gestionar tu entrega.

Ingresa a este enlace único y seguro para:
✅ Ver la ubicación del motorizado en tiempo real
✅ Elegir tu horario aproximado de entrega
✅ Agregar detalles o referencias adicionales
✅ Enviar tu ubicación exacta

🔗 *Enlace de seguimiento:*
www.fastmaxlima.pe/#/entrega/${delivery.id}

💬 *¿Necesitas coordinar tu entrega?*
Comunícate directamente con la logística de Fast Max:
+51 977 802 104

¡Gracias por tu compra! 🌟`;

    window.open(`https://wa.me/+51${phone}?text=${encodeURIComponent(message)}`, '_blank');
  }

  function handleOrderWhatsAppClick(order) {
    const phone = cleanPhone(order.telefono);
    const message = `Hola ${order.customer_name || ''}, coordinamos tu recojo con Fast Max Courier.`;
    window.open(`https://wa.me/+51${phone}?text=${encodeURIComponent(message)}`, '_blank');
  }

  function hasDeliveryLocation(delivery) {
    return delivery.latitude && delivery.longitude;
  }

  function getOrderLocation(order) {
    const lat = order.address?.latitude || order.latitude;
    const lng = order.address?.longitude || order.longitude;
    return lat && lng ? { lat, lng } : null;
  }

  function openLocation(lat, lng) {
    window.open(`https://www.google.com/maps/search/?api=1&query=${lat},${lng}`, '_blank');
  }

  function getStatusColor(status) {
    switch (status) {
      case 'pending': return 'bg-orange-100 text-orange-700 border-orange-200';
      case 'encamino':
      case 'in_transit': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'entregado':
      case 'completed': return 'bg-green-100 text-green-700 border-green-200';
      case 'cancelado':
      case 'rechazado': return 'bg-red-100 text-red-700 border-red-200';
      default: return 'bg-slate-100 text-slate-700 border-slate-200';
    }
  }

  function getStatusLabel(status) {
    const labels = {
      pending: 'Pendiente',
      encamino: 'En Camino',
      in_transit: 'En Camino',
      entregado: 'Entregado',
      completed: 'Completado',
      cancelado: 'Cancelado',
      rechazado: 'Rechazado',
      postergado: 'Postergado',
    };
    return labels[status] || status;
  }

  function handleDownloadExcel() {
    if (deliveryType === 'lima') {
      DownloadExcelAnalytics(filteredDeliveries, selectedDate, userId);
    } else {
      DownloadExcelProvinciaAnalytics(filteredDeliveries, selectedDate);
    }
  }

  async function loadStatusHistory(deliveryId) {
    setStatusHistoryLoading(true);
    setStatusHistoryError('');
    try {
      const response = await fetch(`${apiUrl}/api/delivery-points/${deliveryId}/status-history`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      setStatusHistory(Array.isArray(data.history) ? data.history : []);
    } catch (error) {
      console.error('Error fetching delivery status history:', error);
      setStatusHistoryError('No se pudo cargar el historial.');
    } finally {
      setStatusHistoryLoading(false);
    }
  }

  function handleToggleStatusHistory() {
    const deliveryId = selectedDetail?.item?.id;
    const canShowHistory = selectedDetail?.type === 'lima' && deliveryId;
    if (!canShowHistory) return;

    const nextVisible = !showStatusHistory;
    setShowStatusHistory(nextVisible);
    if (nextVisible && statusHistory.length === 0 && !statusHistoryLoading) {
      loadStatusHistory(deliveryId);
    }
  }

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-10">
        <div className="flex flex-col items-center justify-center">
          <div className="w-12 h-12 border-4 border-brand-primary border-t-transparent rounded-full animate-spin mb-4" />
          <p className="text-sm text-slate-600 font-montserrat">Cargando entregas...</p>
        </div>
      </div>
    );
  }

  const hasAnyDelivery = deliveries.length > 0 || deliveriesProvince.length > 0 || expressDeliveries.length > 0;

  return (
    <div className="space-y-4">
      {hasAnyDelivery && (
        <section className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="bg-brand-primary px-4 py-4 sm:px-6">
            <div className="flex flex-col gap-3">
              <div className="-mx-1 flex items-center gap-2 overflow-x-auto px-1 pb-1">
                <TabButton active={deliveryType === 'lima'} onClick={() => { setDeliveryType('lima'); clearSearch(); }}>
                  Lima ({deliveries.length})
                </TabButton>
                <TabButton active={deliveryType === 'provincia'} onClick={() => { setDeliveryType('provincia'); clearSearch(); }}>
                  Provincia ({deliveriesProvince.length})
                </TabButton>
                {expressDeliveries.length > 0 && (
                  <TabButton active={deliveryType === 'express'} onClick={() => { setDeliveryType('express'); clearSearch(); }}>
                    Express ({expressDeliveries.length})
                  </TabButton>
                )}
              </div>

              {deliveryType !== 'express' ? (
                <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                  <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-center">
                    <h3 className="text-base font-extrabold text-white font-montserrat whitespace-nowrap">
                      Entregas {deliveryType === 'provincia' ? 'Provincia' : 'Lima'} ({filteredDeliveries.length})
                    </h3>
                    <div className="relative w-full sm:w-80">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/60" />
                      <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder={deliveryType === 'provincia' ? 'Buscar ID, nombre, DNI...' : 'Buscar ID, nombre o distrito...'}
                        className="w-full pl-9 pr-9 py-2 bg-white/20 border border-white/30 rounded-lg text-white placeholder-white/60 text-sm font-montserrat focus:outline-none focus:ring-2 focus:ring-white/50 focus:bg-white/30 transition-all"
                      />
                      {searchTerm && (
                        <button onClick={clearSearch} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-white/20 rounded transition-colors" aria-label="Limpiar busqueda">
                          <X className="w-4 h-4 text-white" />
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <HeaderIconButton onClick={handleDownloadExcel} label="Descargar Excel">
                      <FileSpreadsheet className="w-5 h-5" />
                    </HeaderIconButton>
                    {deliveryType === 'lima' && (
                      <HeaderIconButton onClick={() => setActiveComponent('Print')} label="Imprimir Rotulos">
                        <Printer className="w-5 h-5" />
                      </HeaderIconButton>
                    )}
                  </div>
                </div>
              ) : (
                <h3 className="text-base font-extrabold text-white font-montserrat">
                  Entregas Express ({expressDeliveries.length})
                </h3>
              )}
            </div>
          </div>

          <div className="divide-y divide-slate-100">
            {deliveryType !== 'express' && (
              filteredDeliveries.length === 0 ? (
                <EmptyState
                  icon={<Package className="w-10 h-10 text-slate-300" />}
                  title={searchTerm ? 'No se encontraron entregas' : `No hay entregas ${deliveryType === 'provincia' ? 'a provincia' : 'en Lima'}`}
                  description={searchTerm ? `No hay resultados para "${searchTerm}"` : 'No se han registrado entregas para esta fecha'}
                  action={searchTerm ? <button onClick={clearSearch} className="px-4 py-2 bg-brand-primary text-white rounded-lg hover:bg-brand-primary/90 transition-colors font-montserrat text-sm">Limpiar busqueda</button> : null}
                />
              ) : (
                filteredDeliveries.map((delivery) => (
                  <DeliveryRow
                    key={delivery.id}
                    delivery={delivery}
                    deliveryType={deliveryType}
                    canEdit={canEdit}
                    hasLocation={hasDeliveryLocation}
                    getStatusColor={getStatusColor}
                    getStatusLabel={getStatusLabel}
                    onOpen={() => setSelectedDetail({ type: deliveryType, item: delivery })}
                    onWhatsApp={handleWhatsAppClick}
                    onLocation={openLocation}
                    onEdit={onEdit}
                  />
                ))
              )
            )}

            {deliveryType === 'express' && (
              expressDeliveries.length === 0 ? (
                <EmptyState
                  icon={<Zap className="w-10 h-10 text-slate-300" />}
                  title="No hay entregas express"
                  description="No se han registrado servicios express para esta fecha"
                />
              ) : (
                expressDeliveries.map((delivery) => (
                  <ExpressRow
                    key={delivery.id}
                    delivery={delivery}
                    getStatusColor={getStatusColor}
                    getStatusLabel={getStatusLabel}
                    onOpen={() => setSelectedDetail({ type: 'express', item: delivery })}
                  />
                ))
              )
            )}
          </div>
        </section>
      )}

      {orders.length > 0 && (
        <section className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="bg-brand-gold px-4 py-4 sm:px-6">
            <h3 className="text-base font-extrabold text-white font-montserrat">Recojos ({orders.length})</h3>
          </div>
          <div className="divide-y divide-slate-100">
            {orders.map((order) => {
              const location = getOrderLocation(order);
              return (
                <OrderRow
                  key={order.id}
                  order={order}
                  location={location}
                  getStatusColor={getStatusColor}
                  getStatusLabel={getStatusLabel}
                  onOpen={() => setSelectedDetail({ type: 'order', item: order })}
                  onWhatsApp={handleOrderWhatsAppClick}
                  onLocation={openLocation}
                />
              );
            })}
          </div>
        </section>
      )}

      {selectedDetail && (
        <DetailModal
          detail={selectedDetail}
          getStatusColor={getStatusColor}
          getStatusLabel={getStatusLabel}
          onClose={() => setSelectedDetail(null)}
          onShowImages={setSelectedImages}
          statusHistory={statusHistory}
          statusHistoryLoading={statusHistoryLoading}
          statusHistoryError={statusHistoryError}
          showStatusHistory={showStatusHistory}
          onToggleStatusHistory={handleToggleStatusHistory}
        />
      )}

      {selectedImages && (
        <ImageCarouselModal
          images={selectedImages.images}
          deliveryId={selectedImages.deliveryId}
          recipientName={selectedImages.recipientName}
          onClose={() => setSelectedImages(null)}
        />
      )}
    </div>
  );
}

function TabButton({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={`shrink-0 px-4 py-2 rounded-lg font-montserrat font-semibold text-sm transition-all ${
        active ? 'bg-white text-brand-primary shadow-sm' : 'bg-white/20 text-white hover:bg-white/30'
      }`}
    >
      {children}
    </button>
  );
}

function HeaderIconButton({ onClick, label, children }) {
  return (
    <button onClick={onClick} className="p-2 text-white hover:bg-white/20 rounded-lg transition-colors group relative" aria-label={label}>
      {children}
      <span className="absolute top-full right-0 mt-2 px-2 py-1 bg-slate-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none font-montserrat z-50">
        {label}
      </span>
    </button>
  );
}

function RowIconButton({ onClick, disabled, label, children, className }) {
  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        if (!disabled) onClick?.();
      }}
      disabled={disabled}
      className={`h-9 w-9 shrink-0 rounded-lg flex items-center justify-center transition-colors group relative ${className}`}
      aria-label={label}
    >
      {children}
      <span className="absolute top-full right-0 mt-2 px-2 py-1 bg-slate-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none font-montserrat z-50">
        {label}
      </span>
    </button>
  );
}

function DeliveryRow({
  delivery,
  deliveryType,
  canEdit,
  hasLocation,
  getStatusColor,
  getStatusLabel,
  onOpen,
  onWhatsApp,
  onLocation,
  onEdit,
}) {
  const isLima = deliveryType === 'lima';
  const editable = isLima && canEdit(delivery);
  const locationAvailable = isLima && hasLocation(delivery);
  const amount = delivery.charge || delivery.charged || delivery.deposit_advance;
  const area = deliveryType === 'provincia'
    ? [delivery.district, delivery.province].filter(Boolean).join(', ')
    : delivery.district;

  return (
    <button onClick={onOpen} className="w-full px-3 py-3 text-left hover:bg-slate-50 transition-colors sm:px-5">
      <div className="flex items-center gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 min-w-0">
            <span className="shrink-0 text-xs font-bold text-brand-primary bg-brand-primary/10 px-2 py-1 rounded font-montserrat">
              #{delivery.id}
            </span>
            <span className={`shrink-0 px-2 py-0.5 text-[11px] font-semibold rounded-full border font-montserrat ${getStatusColor(delivery.status)}`}>
              {getStatusLabel(delivery.status)}
            </span>
            {delivery.provider_id && (
              <span className="hidden sm:inline-flex shrink-0 px-2 py-0.5 text-[11px] font-semibold rounded-full border bg-indigo-100 text-indigo-700 border-indigo-200 font-montserrat">
                Dropship
              </span>
            )}
          </div>
          <div className="mt-1 grid grid-cols-[minmax(0,1fr)_auto] items-center gap-2">
            <div className="min-w-0">
              <p className="truncate text-sm font-bold text-slate-900 font-montserrat">
                {delivery.recipient_name || 'Sin nombre'}
              </p>
              <p className="truncate text-xs text-slate-500 font-montserrat">
                {area || delivery.recipient_phone || 'Sin ubicacion'}
              </p>
            </div>
            {amount && (
              <span className="hidden sm:inline text-sm font-extrabold text-slate-800 font-montserrat">
                S/ {Number(amount).toFixed(2)}
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-1.5">
          {isLima && (
            <RowIconButton onClick={() => onWhatsApp(delivery)} label="Enviar WhatsApp" className="bg-green-500 text-white hover:bg-green-600">
              <MessageCircle className="w-4 h-4" />
            </RowIconButton>
          )}
          {isLima && (
            <RowIconButton
              onClick={() => onLocation(delivery.latitude, delivery.longitude)}
              disabled={!locationAvailable}
              label={locationAvailable ? 'Ver ubicacion' : 'Sin ubicacion'}
              className={locationAvailable ? 'bg-brand-green text-white hover:bg-brand-green/80' : 'bg-slate-200 text-slate-400 cursor-not-allowed'}
            >
              <Navigation className="w-4 h-4" />
            </RowIconButton>
          )}
          {isLima && (
            <RowIconButton
              onClick={() => onEdit(delivery)}
              disabled={!editable}
              label={editable ? 'Editar' : 'No editable'}
              className={editable ? 'bg-brand-primary text-white hover:bg-brand-primary/90' : 'bg-slate-200 text-slate-400 cursor-not-allowed'}
            >
              <Edit2 className="w-4 h-4" />
            </RowIconButton>
          )}
          <ChevronRight className="hidden sm:block w-4 h-4 text-slate-300" />
        </div>
      </div>
    </button>
  );
}

function ExpressRow({ delivery, getStatusColor, getStatusLabel, onOpen }) {
  return (
    <button onClick={onOpen} className="w-full px-3 py-3 text-left hover:bg-slate-50 transition-colors sm:px-5">
      <div className="flex items-center gap-3">
        <span className="shrink-0 text-xs font-bold text-brand-green bg-brand-green/10 px-2 py-1 rounded font-montserrat">
          #{delivery.id}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="truncate text-sm font-bold text-slate-900 font-montserrat">
              {delivery.recipient_name || 'Sin nombre'}
            </p>
            <span className={`shrink-0 px-2 py-0.5 text-[11px] font-semibold rounded-full border font-montserrat ${getStatusColor(delivery.status)}`}>
              {getStatusLabel(delivery.status)}
            </span>
          </div>
          <p className="truncate text-xs text-slate-500 font-montserrat">
            {delivery.dropoff_address || delivery.pickup_address || 'Sin direccion'}
          </p>
        </div>
        <div className="shrink-0 text-right">
          <p className="text-sm font-extrabold text-brand-green font-montserrat">
            S/ {Number(delivery.total_price || 0).toFixed(2)}
          </p>
          <p className="text-[11px] text-slate-500 font-montserrat">
            {delivery.distance_km} km
          </p>
        </div>
      </div>
    </button>
  );
}

function OrderRow({ order, location, getStatusColor, getStatusLabel, onOpen, onWhatsApp, onLocation }) {
  return (
    <button onClick={onOpen} className="w-full px-3 py-3 text-left hover:bg-slate-50 transition-colors sm:px-5">
      <div className="flex items-center gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="shrink-0 text-xs font-bold text-brand-gold bg-brand-gold/10 px-2 py-1 rounded font-montserrat">
              #{order.id}
            </span>
            <span className={`shrink-0 px-2 py-0.5 text-[11px] font-semibold rounded-full border font-montserrat ${getStatusColor(order.status)}`}>
              {getStatusLabel(order.status)}
            </span>
          </div>
          <p className="mt-1 truncate text-sm font-bold text-slate-900 font-montserrat">
            {order.customer_name || 'Sin nombre'}
          </p>
          <p className="truncate text-xs text-slate-500 font-montserrat">
            {order.address?.address || order.telefono || 'Sin direccion'}
          </p>
        </div>
        <div className="flex items-center gap-1.5">
          {order.telefono && (
            <RowIconButton onClick={() => onWhatsApp(order)} label="Enviar WhatsApp" className="bg-green-500 text-white hover:bg-green-600">
              <MessageCircle className="w-4 h-4" />
            </RowIconButton>
          )}
          <RowIconButton
            onClick={() => onLocation(location.lat, location.lng)}
            disabled={!location}
            label={location ? 'Ver ubicacion' : 'Sin ubicacion'}
            className={location ? 'bg-brand-green text-white hover:bg-brand-green/80' : 'bg-slate-200 text-slate-400 cursor-not-allowed'}
          >
            <Navigation className="w-4 h-4" />
          </RowIconButton>
          <ChevronRight className="hidden sm:block w-4 h-4 text-slate-300" />
        </div>
      </div>
    </button>
  );
}

function DetailModal({
  detail,
  getStatusColor,
  getStatusLabel,
  onClose,
  onShowImages,
  statusHistory,
  statusHistoryLoading,
  statusHistoryError,
  showStatusHistory,
  onToggleStatusHistory,
}) {
  const { type, item } = detail;
  const isOrder = type === 'order';
  const isExpress = type === 'express';
  const isProvincia = type === 'provincia';
  const canShowStatusHistory = type === 'lima';
  const images = item.images || item.image_urls || [];

  const title = isOrder
    ? 'Detalle del Recojo'
    : isExpress
      ? 'Detalle Express'
      : 'Detalle de Entrega';

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-4" onClick={onClose}>
      <div className="max-h-[88vh] w-full max-w-2xl overflow-hidden rounded-t-2xl bg-white shadow-2xl sm:rounded-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-5 py-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-extrabold text-slate-900 font-montserrat">{title}</h2>
              <span className={`px-2 py-0.5 text-xs font-semibold rounded-full border font-montserrat ${getStatusColor(item.status)}`}>
                {getStatusLabel(item.status)}
              </span>
              {canShowStatusHistory && (
                <button
                  onClick={onToggleStatusHistory}
                  className={`relative rounded-lg p-2 transition-colors ${
                    showStatusHistory
                      ? 'bg-brand-primary text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                  aria-label="Ver historial de estado"
                  title="Ver historial de estado"
                >
                  <History className="h-4 w-4" />
                </button>
              )}
            </div>
            <p className="text-sm text-slate-500 font-montserrat">ID: {item.id}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg transition-colors" aria-label="Cerrar">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        <div className="max-h-[calc(88vh-4.5rem)] overflow-y-auto px-5 py-4">
          {isOrder ? (
            <DetailGrid>
              <DetailLine icon={<User />} label="Cliente" value={item.customer_name} />
              <DetailLine icon={<Phone />} label="Telefono" value={item.telefono} />
              <DetailLine icon={<MapPin />} label="Direccion" value={item.address?.address} wide />
              <DetailLine icon={<FileText />} label="Notas" value={item.order_notes} wide />
            </DetailGrid>
          ) : isExpress ? (
            <DetailGrid>
              <DetailLine icon={<User />} label="Destinatario" value={item.recipient_name} />
              <DetailLine icon={<Phone />} label="Telefono" value={item.recipient_phone} />
              <DetailLine icon={<MapPin />} label="Recojo" value={item.pickup_address} wide />
              <DetailLine icon={<Navigation />} label="Destino" value={item.dropoff_address} wide />
              <DetailLine icon={<Zap />} label="Distancia" value={item.distance_km ? `${item.distance_km} km` : null} />
              <DetailLine icon={<DollarSign />} label="Total" value={`S/ ${Number(item.total_price || 0).toFixed(2)}`} />
              {item.items?.length > 0 && (
                <DetailLine icon={<Package />} label="Productos" value={item.items.map((i) => `${i.quantity}x ${i.product_sku}`).join(', ')} wide />
              )}
            </DetailGrid>
          ) : (
            <DetailGrid>
              <DetailLine icon={<User />} label="Destinatario" value={item.recipient_name} />
              <DetailLine icon={<Phone />} label="Telefono" value={item.recipient_phone} />
              <DetailLine icon={<MapPin />} label={isProvincia ? 'Destino' : 'Direccion'} value={isProvincia ? [item.district, item.province].filter(Boolean).join(', ') : [item.address, item.district].filter(Boolean).join(', ')} wide />
              {isProvincia && <DetailLine icon={<Package />} label="Agencia" value={item.agency_address} wide />}
              {isProvincia && <DetailLine icon={<FileText />} label="Codigo recojo" value={item.agency_pickup_code} />}
              {isProvincia && <DetailLine icon={<FileText />} label="DNI" value={item.recipient_dni} />}
              <DetailLine icon={<Package />} label="Producto" value={item.product} wide />
              <DetailLine icon={<FileText />} label="Notas" value={item.notes} wide />
              <DetailLine icon={<Truck />} label="Notas driver" value={item.notes_driver} wide />
              <DetailLine icon={<DollarSign />} label="Por cobrar" value={item.charge ? `S/ ${Number(item.charge).toFixed(2)}` : null} />
              <DetailLine icon={<DollarSign />} label="Cobrado" value={item.charged ? `S/ ${Number(item.charged).toFixed(2)}` : null} />
              <DetailLine icon={<DollarSign />} label="Anticipo" value={item.deposit_advance ? `S/ ${Number(item.deposit_advance).toFixed(2)}` : null} />
              <DetailLine icon={<Clock />} label="Llegada" value={item.arrival_time ? new Date(item.arrival_time).toLocaleString('es-PE', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : null} />
              {images.length > 0 && (
                <div className="sm:col-span-2">
                  <button
                    onClick={() => onShowImages({ images, deliveryId: item.id, recipientName: item.recipient_name })}
                    className="inline-flex items-center gap-2 rounded-lg bg-brand-primary/10 px-3 py-2 text-sm font-semibold text-brand-primary transition-colors hover:bg-brand-primary/20 font-montserrat"
                  >
                    <ImageIcon className="w-4 h-4" />
                    Ver {images.length} {images.length === 1 ? 'imagen' : 'imagenes'}
                  </button>
                </div>
              )}
              {canShowStatusHistory && showStatusHistory && (
                <div className="sm:col-span-2">
                  <StatusHistoryTimeline
                    history={statusHistory}
                    loading={statusHistoryLoading}
                    error={statusHistoryError}
                    getStatusLabel={getStatusLabel}
                    getStatusColor={getStatusColor}
                  />
                </div>
              )}
            </DetailGrid>
          )}
        </div>
      </div>
    </div>
  );
}

function DetailGrid({ children }) {
  return <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">{children}</div>;
}

function DetailLine({ icon, label, value, wide = false }) {
  if (value === null || value === undefined || value === '') return null;

  return (
    <div className={`rounded-lg border border-slate-200 bg-slate-50 p-3 ${wide ? 'sm:col-span-2' : ''}`}>
      <div className="mb-1 flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-slate-500 font-montserrat">
        {React.cloneElement(icon, { className: 'w-3.5 h-3.5' })}
        {label}
      </div>
      <p className="break-words text-sm font-semibold text-slate-900 font-montserrat">{value}</p>
    </div>
  );
}

function StatusHistoryTimeline({ history, loading, error, getStatusLabel, getStatusColor }) {
  if (loading) {
    return (
      <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
        <div className="flex items-center gap-3 text-sm font-semibold text-slate-600 font-montserrat">
          <div className="h-4 w-4 rounded-full border-2 border-brand-primary border-t-transparent animate-spin" />
          Cargando historial...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4">
        <div className="flex items-center gap-2 text-sm font-semibold text-red-700 font-montserrat">
          <AlertCircle className="h-4 w-4" />
          {error}
        </div>
      </div>
    );
  }

  if (!history.length) {
    return (
      <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
        <p className="text-sm font-semibold text-slate-600 font-montserrat">Sin cambios registrados.</p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="mb-3 flex items-center gap-2 text-xs font-bold uppercase text-slate-500 font-montserrat">
        <History className="h-3.5 w-3.5" />
        Historial de estado
      </div>
      <div className="space-y-3">
        {history.map((entry) => (
          <StatusHistoryItem
            key={entry.id}
            entry={entry}
            getStatusLabel={getStatusLabel}
            getStatusColor={getStatusColor}
          />
        ))}
      </div>
    </div>
  );
}

function StatusHistoryItem({ entry, getStatusLabel, getStatusColor }) {
  const changedAt = entry.created_at
    ? new Date(entry.created_at).toLocaleString('es-PE', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    })
    : 'Sin fecha';
  const fromDate = formatShortDate(entry.from_delivery_date);
  const toDate = formatShortDate(entry.to_delivery_date);
  const actor = entry.changed_by_username || sourceLabel(entry.source) || entry.actor_role;

  return (
    <div className="relative pl-5">
      <span className="absolute left-0 top-1.5 h-2.5 w-2.5 rounded-full bg-brand-primary" />
      <div className="flex flex-wrap items-center gap-2">
        {entry.from_status && (
          <span className={`px-2 py-0.5 text-[11px] font-semibold rounded-full border font-montserrat ${getStatusColor(entry.from_status)}`}>
            {getStatusLabel(entry.from_status)}
          </span>
        )}
        {entry.from_status && <ChevronRight className="h-3.5 w-3.5 text-slate-400" />}
        <span className={`px-2 py-0.5 text-[11px] font-semibold rounded-full border font-montserrat ${getStatusColor(entry.to_status)}`}>
          {getStatusLabel(entry.to_status)}
        </span>
      </div>
      <p className="mt-1 text-xs font-semibold text-slate-500 font-montserrat">
        {changedAt}{actor ? ` - ${actor}` : ''}
      </p>
      {fromDate && toDate && fromDate !== toDate && (
        <p className="mt-1 text-xs text-slate-600 font-montserrat">
          Fecha: {fromDate} -> {toDate}
        </p>
      )}
      {(entry.reason || entry.note) && (
        <p className="mt-1 break-words text-sm font-semibold text-slate-800 font-montserrat">
          {entry.reason || entry.note}
        </p>
      )}
    </div>
  );
}

function formatShortDate(value) {
  if (!value) return '';
  return new Date(value).toLocaleDateString('es-PE', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function sourceLabel(source) {
  const labels = {
    admin_postpone: 'Admin',
    admin_single_status: 'Admin',
    admin_bulk_status: 'Admin',
    admin_delivery_point_edit: 'Admin',
    driver_submit: 'Driver',
    driver_start_route: 'Driver',
    driver_route_eta: 'Driver',
    delivery_point_patch: 'Usuario',
    backfill: 'Sistema',
  };
  return labels[source] || null;
}

function EmptyState({ icon, title, description, action = null }) {
  return (
    <div className="p-10 text-center">
      <div className="mx-auto mb-4 flex justify-center">{icon}</div>
      <h3 className="text-lg font-bold text-slate-900 font-montserrat mb-2">{title}</h3>
      <p className="text-sm text-slate-600 font-montserrat mb-4">{description}</p>
      {action}
    </div>
  );
}

export default DeliveryList;
